import { DateTime } from 'luxon';
import ordinal from 'ordinal';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Chooser from '../../../../components/Chooser';
import useShippingOptions from '../../../../hooks/useShippingOptions';
import * as types from '../../../../types';
import ShippingOptionsItem from './ShippingOptionsItem';
import styles from './ShippingOptionsList.module.scss';
import { useLocale } from '../../../../contexts/LocaleContext';
import { getLocalizedPricing } from '../../../../lib/util/money';

interface ShippingOptionsListProps {
  anyPreorder: boolean;
  canExpedite: boolean;
  currentlySelected: types.ShippingCode | undefined;
  onSelect: (shippingCode: types.ShippingCode) => void;
  showHeader?: boolean;
  /** checkout v2: the ground and 2-day options read by what they do, with no arrival date */
  compact?: boolean;
  /** a gift order's compact rows take the gifting design's darker borders */
  gift?: boolean;
}

// the two options the hardware-first arm sells in the us; any other code keeps the server's name
const COMPACT_LABELS: Partial<Record<types.ShippingCode, { name: string; description?: string }>> = {
  'so-free': { name: 'hardware_first_shipping_ground' },
  'so-2day': { name: 'hardware_first_shipping_expedited', description: 'hardware_first_shipping_expedited_sub' },
};

export default function ShippingOptionsList({
  anyPreorder,
  canExpedite,
  currentlySelected,
  onSelect,
  showHeader = true,
  compact = false,
  gift = false,
}: ShippingOptionsListProps) {
  const { t } = useTranslation();
  const { shippingOptions } = useShippingOptions();
  const { currency } = useLocale();
  const availableShippingCodesSet = useMemo(
    () => new Set(shippingOptions.filter(({ isExpedited }) => !isExpedited || canExpedite).map((so) => so.code)),
    [shippingOptions, canExpedite],
  );

  useEffect(() => {
    // Pre-select the first available shipping option (typically free ground shipping)
    if (availableShippingCodesSet.size >= 1 && !currentlySelected) {
      onSelect(Array.from(availableShippingCodesSet)[0]);
    }
  }, [availableShippingCodesSet, currentlySelected, onSelect, shippingOptions]);

  const disclaimer: string | undefined = useMemo(() => {
    const shippingOption = currentlySelected
      ? shippingOptions.find((so) => so.code === currentlySelected)
      : shippingOptions[0];

    if (!canExpedite) {
      return t('expedited_shipping_not_available_for_po_box_addresses_or_us_territories');
    } else if (shippingOption?.arrivesOn && !anyPreorder && !compact) {
      // arrivesOn is a ISO string set to midnight US/Eastern of the day it arrives, so we should only care about
      // date and not try to convert it to any timezone. setZone will keep it in the original timezone.
      const arrivesOnDate = DateTime.fromISO(shippingOption.arrivesOn, { setZone: true });
      const ordinalDay = ordinal(arrivesOnDate.day);
      const formattedDate = arrivesOnDate.toFormat(`cccc, LLLL '${ordinalDay}'`); // Friday, August 6th

      return t('this_order_will_arrive_on_date', { date: formattedDate });
    }
  }, [currentlySelected, anyPreorder, shippingOptions, canExpedite, compact, t]);

  return (
    <>
      <div
        className={classNames(styles.main, {
          [styles.compact]: !showHeader,
          [styles.compactRows]: compact,
          [styles.giftRows]: compact && gift,
        })}
      >
        {showHeader && (
          <div className={styles.shippingHeader}>
            <h4>{t('shipping_method')}</h4>
          </div>
        )}
        <Chooser
          onSelect={(code) => {
            if (availableShippingCodesSet.has(code)) {
              onSelect(code);
            }
          }}
          selectedOption={currentlySelected}
          groups={[
            {
              footer: <div className={styles.shippingDisclaimer}>{disclaimer}</div>,
              options: shippingOptions.map(({ code, name, pricingInCents }) => {
                const disabled = !availableShippingCodesSet.has(code);
                const priceInCents = getLocalizedPricing(pricingInCents, currency);
                const label = compact ? COMPACT_LABELS[code] : undefined;

                return {
                  value: code,
                  disabled,
                  content: (
                    <ShippingOptionsItem
                      key={code}
                      disabled={disabled}
                      name={label ? t(label.name) : name}
                      description={label?.description && t(label.description)}
                      priceInCents={priceInCents}
                      compact={compact}
                    />
                  ),
                };
              }),
            },
          ]}
          showRadio
        />
      </div>
    </>
  );
}
